import { useState, useEffect } from 'react';
import api from '../../../api/axios';
import type { TimelineEvent } from '../types/timeline.types';

// Category mappings for consolidated filters
const ENTITY_EVENTS = [
  'entity_added',
  'entity_removed',
  'entities_bulk_added',
  'entities_bulk_removed',
];

const DEFENDANT_EVENTS = [
  'defendant_added',
  'defendant_removed',
  'defendants_bulk_added',
  'defendants_bulk_removed',
];

const DOCUMENT_EVENTS = [
  'document_generated',
  'document_bundle_generated',
  'document_uploaded',
  'document_edited',
  'document_removed',
];

const TASK_EVENTS = [
  'task_created',
  'task_assigned',
  'task_completed',
  'task_status_changed',
];

const TEAM_EVENTS = [
  'counsel_added',
  'counsel_removed',
  'team_member_added',
  'team_member_removed',
];

const EMAIL_EVENTS = [
  'email_thread_added',
  'email_received',
  'email_sent',
];

interface UseTimelineEventsProps {
  caseId: string;
  headers: any;
}

export const useTimelineEvents = ({ caseId, headers }: UseTimelineEventsProps) => {
  const [events, setEvents] = useState<TimelineEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  const [selectedFilters, setSelectedFilters] = useState<string[]>([
    'log_entry',
    'tasks',
    'entities',
    'defendants',
    'team',
    'case_created',
    'status_changed',
    'emails',
  ]); // All checked by default except documents
  const [filterAnchorEl, setFilterAnchorEl] = useState<HTMLElement | null>(null);
  const [expandedEvents, setExpandedEvents] = useState<Set<string>>(new Set());
  const [expandedQuotedText, setExpandedQuotedText] = useState<Set<string>>(new Set());
  const [expandedEmailDetails, setExpandedEmailDetails] = useState<Set<string>>(new Set());

  // Fetch timeline events with lazy loading
  const fetchTimelineEvents = async (loadMore = false) => {
    if (loadMore) {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }
    setError(null);

    try {
      // Always fetch all events and filter client-side for multi-select
      const params = new URLSearchParams({
        page: '1',
        page_size: '1000',
      });

      const response = await api.get(`/cases/${caseId}/timeline/?${params.toString()}`, { headers });

      let fetchedEvents = response.data.events || [];

      // Client-side filtering based on selected categories
      fetchedEvents = fetchedEvents.filter((event: TimelineEvent) => {
        // Check if event matches any selected filter
        if (selectedFilters.includes('log_entry') && event.event_type === 'log_entry') return true;
        if (selectedFilters.includes('tasks') && TASK_EVENTS.includes(event.event_type)) return true;
        if (selectedFilters.includes('documents') && DOCUMENT_EVENTS.includes(event.event_type)) return true;
        if (selectedFilters.includes('entities') && ENTITY_EVENTS.includes(event.event_type)) return true;
        if (selectedFilters.includes('defendants') && DEFENDANT_EVENTS.includes(event.event_type)) return true;
        if (selectedFilters.includes('team') && TEAM_EVENTS.includes(event.event_type)) return true;
        if (selectedFilters.includes('case_created') && event.event_type === 'case_created') return true;
        if (selectedFilters.includes('status_changed') && event.event_type === 'status_changed') return true;
        if (selectedFilters.includes('emails') && EMAIL_EVENTS.includes(event.event_type)) return true;
        return false;
      });

      setEvents(fetchedEvents);
      setHasMore(false); // No pagination when fetching all

      // Return the fetched events for use in calling code
      return fetchedEvents;
    } catch (err) {
      console.error('Failed to fetch timeline events:', err);
      setError('Failed to load timeline events');
      return [];
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const loadMoreEvents = () => {
    if (!loadingMore && hasMore) {
      setPage(prev => prev + 1);
    }
  };

  const toggleEventExpanded = (eventId: string) => {
    setExpandedEvents(prev => {
      const newSet = new Set(prev);
      if (newSet.has(eventId)) {
        newSet.delete(eventId);
      } else {
        newSet.add(eventId);
      }
      return newSet;
    });
  };

  const handleFilterToggle = (filter: string) => {
    setSelectedFilters(prev => {
      if (prev.includes(filter)) {
        return prev.filter(f => f !== filter);
      } else {
        return [...prev, filter];
      }
    });
  };

  const handleSelectAll = () => {
    setSelectedFilters([
      'log_entry', 'tasks', 'documents', 'entities', 'defendants',
      'team', 'case_created', 'status_changed', 'emails'
    ]);
  };

  const handleDeselectAll = () => {
    setSelectedFilters([]);
  };

  const handleFilterMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setFilterAnchorEl(event.currentTarget);
  };

  const handleFilterMenuClose = () => {
    setFilterAnchorEl(null);
  };

  const handleCollapseAll = () => {
    setExpandedEvents(new Set());
  };

  // Fetch events on mount and filter change
  useEffect(() => {
    if (caseId) {
      fetchTimelineEvents(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseId, selectedFilters]);

  // Guard to close menu if anchor element disappears
  useEffect(() => {
    if (filterAnchorEl && !document.body.contains(filterAnchorEl)) {
      setFilterAnchorEl(null);
    }
  }, [filterAnchorEl]);

  return {
    // State
    events,
    loading,
    loadingMore,
    error,
    hasMore,
    page,
    selectedFilters,
    filterAnchorEl,
    expandedEvents,
    expandedQuotedText,
    expandedEmailDetails,

    // Functions
    fetchTimelineEvents,
    loadMoreEvents,
    toggleEventExpanded,
    handleFilterToggle,
    handleSelectAll,
    handleDeselectAll,
    handleFilterMenuOpen,
    handleFilterMenuClose,
    handleCollapseAll,
    setExpandedQuotedText,
    setExpandedEmailDetails,

    // Constants
    ENTITY_EVENTS,
    DEFENDANT_EVENTS,
    DOCUMENT_EVENTS,
    TASK_EVENTS,
    TEAM_EVENTS,
    EMAIL_EVENTS,
  };
};
